import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StyleSheet, Text, View } from './base_components';
import { BusyOverlay, useBusy } from './busy_component';

import PropList from './prop_list';
import StandardBoxList from './standard_box_list';
import TextButton from './buttons/text_button';
import TitleActionLine from './title_action_line';
import TabBar from './tab_bar';

import AuditStore from '../stores/audit_store';
import DirStore from '../stores/dir_store';
import MediaStore from '../stores/media_store';

import { byteFormat, dateFormat, relativeDate } from '../tools/util';

const styles = StyleSheet.create({
  mediaFile: { minWidth: 0, alignSelf: 'stretch', flexDirection: 'column' },
  deleted: { color: 'red' },
  button: { marginRight: '1rem' },
  tabBar: { marginLeft: '2.2rem', marginRight: '2.2rem' },
  actionButton: { marginLeft: '1rem' },
  emptyListText: { margin: '2rem', color: '#666', fontSize: '1.5rem' },
});
const TAB_LIST = [
  { value: 'properties', text: 'Properties' },
  { value: 'versions', text: 'Versions' },
];
type Props = { org: any; mediaGrant: any; path: string; media: any };
export default function MediaFile(props: Props) {
  const { media, org, path } = props;
  const media_id = media?.media_id;
  const media_grant_id = props.mediaGrant?.media_grant_id;
  const grant_name = props.mediaGrant?.grant_name;
  const [is_busy, setBusy, clearBusy] = useBusy();
  const [tab, setTab] = React.useState('properties');
  const version_media = MediaStore.useMedia(media_grant_id, media_id);
  const [selected_versions, setSelectedVersions] = React.useState([]);
  const navigate = useNavigate();

  const parts = media?.media_path?.split?.('/');
  const file_name = parts?.pop?.() ?? path.split('/').pop();
  const file_parts = file_name.split('.');
  const ext = file_parts.length > 1 ? '.' + file_parts.pop() : '';
  const name = file_parts.join('.');
  const dir = parts?.join?.('/') || '/';

  React.useEffect(() => {
    MediaStore.fetch({ org_id: org?.org_id, media_grant_id, media_id });
  }, [org?.org_id, media_grant_id, media_id]);

  function _onUndeletePress() {
    if (setBusy()) {
      const opts = { org_id: org?.org_id, media_grant_id, media_id };
      MediaStore.undelete(opts, (err) => {
        if (err) {
          window.alert('Undelete failed');
        }
        DirStore.fetch({ org_id: org?.org_id, media_grant_id, path });
        clearBusy();
      });
    }
  }
  function _onDeletePress() {
    const confirmed = window.confirm(
      'Are you sure you want to delete this file?'
    );
    if (confirmed && setBusy()) {
      const opts = { org_id: org?.org_id, media_grant_id, media_id };
      MediaStore.del(opts, (err) => {
        if (err) {
          window.alert('Delete failed');
        }
        DirStore.fetch({ org_id: org?.org_id, media_grant_id, path });
        clearBusy();
      });
    }
  }
  function _onDownloadPress(inline?: boolean) {
    if (setBusy()) {
      const opts = { org_id: org?.org_id, media_grant_id, media_id, inline };
      MediaStore.getUrl(opts, (err, url) => {
        clearBusy();
        if (err === 404) {
          window.alert('File not uploaded, check back later.');
        } else if (err === 403) {
          window.alert(
            'Sorry, you do not have permission to download this file.'
          );
        } else if (err) {
          window.alert('Failed to get download url, please try again.');
        } else if (inline) {
          window.open(url, '_blank');
        } else {
          _downloadFile(url);
        }
      });
    }
  }
  function _onVersionPress(version, inline?: boolean, restore?: boolean) {
    if (version && setBusy()) {
      const { media_version_id, file_hash, file_size } = version;
      if (restore) {
        const create_time = dateFormat(version.create_time);
        const opts = {
          org_id: org?.org_id,
          media_grant_id,
          file_hash,
          file_size,
          dir,
          name,
          ext,
          create_time,
        };
        MediaStore.restoreMedia(opts, (err, new_path) => {
          clearBusy();
          if (err) {
            window.alert('Failed to get restore file, please try again.');
          } else {
            navigate(`/home/${grant_name}${new_path}`);
          }
        });
      } else {
        const opts = {
          org_id: org?.org_id,
          media_grant_id,
          media_version_id,
          inline,
        };
        MediaStore.getVersionUrl(opts, (err, url) => {
          clearBusy();
          if (err === 404) {
            window.alert('File not uploaded, check back later.');
          } else if (err === 403) {
            window.alert(
              'Sorry, you do not have permission to download this file.'
            );
          } else if (err) {
            window.alert('Failed to get download url, please try again.');
          } else if (inline) {
            window.open(url, '_blank');
          } else {
            _downloadFile(url);
          }
        });
      }
    }
  }
  const tab_list = TAB_LIST.slice();
  if (org?.is_admin) {
    tab_list.push({ value: 'audit', text: 'Audit Tracking' });
  }
  let content: any = null;
  if (!media) {
    content = (
      <StandardBoxList title='File Detail'>
        <Text style={styles.emptyListText}>File Not Found</Text>
      </StandardBoxList>
    );
  } else if (tab === 'properties') {
    content = (
      <TabProperties
        fileName={file_name}
        media={media}
        versionMedia={version_media}
      />
    );
  } else if (tab === 'versions') {
    content = (
      <TabVersions
        media={version_media}
        selected={selected_versions}
        onChange={setSelectedVersions}
        onPress={_onVersionPress}
      />
    );
  } else {
    content = <TabAudit media={media} org={org} />;
  }

  return (
    <>
      <View style={styles.mediaFile}>
        <TitleActionLine title={file_name}>
          {media ? (
            <>
              <TextButton
                style={styles.button}
                text='Open'
                onPress={_onDownloadPress.bind(null, true)}
              />
              <TextButton
                style={styles.button}
                text='Download'
                onPress={_onDownloadPress.bind(null, false)}
              />
              {media?.is_deleted ? (
                <TextButton text='Undelete' onPress={_onUndeletePress} />
              ) : (
                <TextButton
                  type='danger'
                  text='Delete'
                  onPress={_onDeletePress}
                />
              )}
            </>
          ) : null}
        </TitleActionLine>
        {media ? (
          <TabBar
            style={styles.tabBar}
            value={tab}
            list={tab_list}
            onChange={setTab}
          />
        ) : null}
        {content}
      </View>
      <BusyOverlay isBusy={is_busy} />
    </>
  );
}
function TabProperties(props) {
  const { fileName, media, versionMedia } = props;
  const rows = versionMedia?.hash_containers?.map?.((item) => {
    return [item.device_name, relativeDate(item.last_seen) || '-'];
  });
  return (
    <>
      <StandardBoxList title='File Detail'>
        <PropList
          columns={[
            [
              { label: 'File Name', value: fileName },
              { label: 'Full Path', value: media?.media_path },
              { label: 'Size', value: byteFormat(media?.file_size || 0) },
              { label: 'Size (Bytes)', value: media?.file_size },
              {
                label: 'Status',
                value: media?.is_deleted ? 'Deleted' : 'Active',
                style: media?.is_deleted ? styles.deleted : null,
              },
            ],
            [
              { label: 'Media ID', value: media?.media_id },
              { label: 'File Hash', value: media?.file_hash },
              { label: 'Last Updated', value: dateFormat(media?.last_updated) },
              {
                label: 'Last Modified By',
                value:
                  versionMedia?.last_update_user_name ??
                  versionMedia?.last_update_device_name ??
                  media?.last_update_device_id ??
                  media?.last_update_user_id,
              },
              {
                label: 'Network Status',
                value: versionMedia?.in_s3 ? 'In Cloud' : 'Not Uploaded',
              },
            ],
          ]}
        />
      </StandardBoxList>
      <StandardBoxList
        title='Devices With Media'
        detail={versionMedia?.hash_containers?.length ?? 0}
        headers={['Device Name', 'Last Seen']}
        rows={rows}
      >
        {versionMedia?.hash_containers?.length === 0 ? (
          <Text style={styles.emptyListText}>Not found on any devices.</Text>
        ) : null}
      </StandardBoxList>
    </>
  );
}
function TabVersions(props) {
  const { media, selected, onChange, onPress } = props;
  const rows = media?.version_list?.map?.((version) => {
    return [
      dateFormat(version.create_time),
      version.file_hash ? byteFormat(version.file_size) : '-',
      version.file_hash || (version.is_deleted ? 'Delete' : 'Undelete'),
      version.user_name ?? version.device_name ?? '-',
    ];
  });
  function _onPress(inline?: boolean, restore?: boolean) {
    const version = media?.version_list?.[selected?.[0]];
    onPress(version, inline ?? false, restore ?? false);
  }
  const has_selected_hash = media?.version_list?.[selected?.[0]]?.file_hash;
  const buttons = (
    <>
      <TextButton
        style={styles.actionButton}
        text='Open'
        disabled={!has_selected_hash}
        onPress={_onPress.bind(true)}
      />
      <TextButton
        style={styles.actionButton}
        text='Download'
        disabled={!has_selected_hash}
        onPress={_onPress}
      />
      <TextButton
        style={styles.actionButton}
        text='Restore to Folder'
        disabled={!has_selected_hash}
        onPress={_onPress.bind(false, true)}
      />
    </>
  );
  return (
    <StandardBoxList
      title='Versions'
      detail={media?.version_list?.length ?? 0}
      buttons={buttons}
      headers={['Create Date', 'Size', 'File Hash', 'Modified By']}
      sizes={[2, 1, 4, 2]}
      rows={rows}
      selected={selected}
      singleSelect={true}
      onChange={onChange}
    />
  );
}
function TabAudit(props) {
  const { media, org } = props;
  const audit_list = AuditStore.useMediaList(media?.media_id);
  React.useEffect(() => {
    AuditStore.fetchMedia({ media_id: media?.media_id, org_id: org?.org_id });
  }, [media?.media_id, org?.org_id]);
  const rows = audit_list?.map?.((item) => {
    return [
      dateFormat(item.create_time),
      item.device_name || item.user_name || item.email,
      item.dest_path || 'Web Download',
    ];
  });
  return (
    <StandardBoxList
      title='Audit Tracking'
      detail={audit_list?.length ?? 0}
      headers={['Timestamp', 'User', 'Destination']}
      sizes={[1, 2, 3]}
      rows={rows}
    >
      {audit_list?.length === 0 ? (
        <Text style={styles.emptyListText}>No audit events found.</Text>
      ) : null}
    </StandardBoxList>
  );
}
function _downloadFile(url) {
  const a = document.createElement('a');
  a.href = url;
  a.download = url.split('/').pop();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
